import Devise from "@kaspernj/api-maker/build/devise"
import {digg} from "diggerize"
import ModelEvents from "@kaspernj/api-maker/build/model-events"
import React from "react"
import {Subscription} from "models"
import unsupportedProps from "components/utils/unsupported-props"
import useShape from "set-state-compare/build/use-shape"

/** @param {import("models/user").default | undefined} currentUser */
const hasAlignedSignedInUser = (currentUser) => {
  const deviseCurrentUser = Devise.currentUser()

  return Boolean(
    currentUser?.id?.() &&
    deviseCurrentUser?.id?.() &&
    Devise.isUserSignedIn() &&
    currentUser.id() == deviseCurrentUser.id()
  )
}

/** @param {object} s */
const loadHasPro = async (s) => {
  if (!hasAlignedSignedInUser(s.p.user)) {
    s.set({
      expiredTrialSubscription: null,
      hasPro: false,
      loaded: true,
      userSubscription: null,
      userSubscriptionPlanIdentifier: undefined
    })

    return
  }

  const result = await Subscription.hasPro({school_id: s.p.schoolId, school_class_id: s.p.schoolClassId, user_id: s.m.userId})
  const hasPro = digg(result, "has_pro")
  const expiredTrialSubscription = digg(result, "pro_expired_trial_subscription")
  const userSubscription = digg(result, "user_subscription")
  const userSubscriptionPlanIdentifier = digg(result, "user_subscription_plan_identifier")

  s.set({
    expiredTrialSubscription,
    loaded: true,
    hasPro,
    userSubscription,
    userSubscriptionPlanIdentifier
  })
}

const useHasPro = ({schoolId, schoolClassId, user, ...restProps}) => {
  unsupportedProps(restProps)

  const s = useShape({schoolId, schoolClassId, user})

  s.useStates({
    expiredTrialSubscription: undefined,
    hasPro: undefined,
    lastChanged: () => new Date(),
    loaded: false,
    userSubscription: undefined,
    userSubscriptionPlanIdentifier: undefined
  })

  s.updateMeta({userId: user?.id()})

  const onStartProUser = React.useCallback(() => {
    loadHasPro(s)
  }, [])

  const onStartProUserTrial = React.useCallback(() => {
    loadHasPro(s)
  }, [])

  React.useMemo(() => {
    if (hasAlignedSignedInUser(s.p.user)) {
      loadHasPro(s)
    } else if (s.s.hasPro !== undefined) {
      s.set({
        expiredTrialSubscription: null,
        hasPro: false,
        loaded: true,
        userSubscription: null,
        userSubscriptionPlanIdentifier: undefined
      })
    }
  }, [schoolId, schoolClassId, s.m.userId, s.s.lastChanged])

  React.useEffect(() => {
    if (hasAlignedSignedInUser(s.p.user)) {
      const webSocketSubscription = ModelEvents.connect(s.p.user, "start_pro_user", onStartProUser)

      return () => {
        webSocketSubscription.unsubscribe()
      }
    }
  }, [s.p.user?.id()])

  React.useEffect(() => {
    if (hasAlignedSignedInUser(s.p.user)) {
      const webSocketSubscription = ModelEvents.connect(s.p.user, "start_pro_user_trial", onStartProUserTrial)

      return () => {
        webSocketSubscription.unsubscribe()
      }
    }
  }, [s.p.user?.id()])

  return {
    expiredTrialSubscription: s.s.expiredTrialSubscription,
    hasPro: s.s.hasPro,
    loaded: s.s.loaded,
    userSubscription: s.s.userSubscription,
    userSubscriptionPlanIdentifier: s.s.userSubscriptionPlanIdentifier
  }
}

export default useHasPro
